/* oxlint-disable import/max-dependencies, max-lines -- session orchestrator: one cohesive lifecycle state machine driving a single `claude` PTY across turns (spawn, feed, park, reuse, teardown); it aggregates the pty submodules (queue, tailer, hook socket, spawn args, settings, coalescer, tool server) and the pure helpers are already split out to pty-support.ts */
/**
 * PtySession — orchestrates one `claude` CLI run under a pseudo-terminal.
 *
 * It merges two event sources into a single stream:
 *   1. the on-disk transcript JSONL (tailed via JsonlTailer), and
 *   2. PostToolUse progress envelopes relayed over a Unix socket.
 *
 * Raw PTY stdout is never turned into HarnessEvents — only the trusted
 * transcript file and the validated socket envelopes become events. When a
 * PtyBridge is supplied, that raw stdout is instead mirrored verbatim to the
 * S2 relay so the S5 "Connected TUI" terminal can render it, and relayed
 * keystrokes / resizes are fed back into the pty.
 *
 * `node-pty` is loaded lazily so that SDK-only consumers never pull in the
 * native module.
 */

import { randomUUID } from "node:crypto";
import { mkdtemp, mkdir, rm, writeFile } from "node:fs/promises";
import { join, dirname } from "node:path";
import { stageOpenCodePlugin } from "../opencode/plugin.js";
import { OpenCodeEventSource } from "../opencode/event-source.js";
import type { PtyChatEventPayload } from "@project/shared";
import type {
  HarnessEvent,
  HarnessQueryOptions,
  HarnessUserMessage,
  HarnessUserQuestion,
  PtyBridge,
} from "../types.js";
import { AsyncEventQueue } from "./event-queue.js";
import {
  HookSocketServer,
  type ToolProgress,
  type PreToolUseRequest,
  type PreToolUseVerdict,
} from "./hook-socket.js";
import { JsonlTailer } from "./jsonl-tailer.js";
import { matchUsageLimitBanner } from "./limit-banner.js";
import {
  mapChatRecords,
  compactQuestionsJson,
  isBackgroundTaskNotificationRecord,
} from "./chat-record-mapper.js";
import { writeHookSettings, sessionTranscriptPath } from "./settings.js";
import { cleanTerminalOutput } from "./spawn-args.js";
import { redact } from "../../execution/redactor.js";
import { PtyOutputCoalescer } from "./output-coalescer.js";
import { PtyToolServer, startToolServers } from "./tool-server.js";
import { ClaudeTuiAdapter } from "./adapters/claude.js";
import type { TuiAdapter } from "./adapters/types.js";
import {
  resolvePtySpawn,
  sessionTempBase,
  inheritedEnv,
  buildPromptBytes,
  renderPromptContentText,
  sleep,
  transcriptSize,
  parseUserQuestions,
  turnOptionsFrom,
  MAX_DIAGNOSTIC_OUTPUT,
  MAX_BETWEEN_TURN_BUFFER,
  resolveSubmitSettleMs,
  resolveSubmitNudgeTiming,
  resolvePlanDialogTiming,
  resolveRawTuiProbeTiming,
  sentinelEchoed,
  sawTerminalSetup as detectTerminalSetup,
  needsRawReadyGate,
  killPtyWithEscalation,
  type PtyProcess,
  type TurnOptions,
} from "./pty-support.js";

// Re-exported for tests and callers that imported them from this module before
// the pure helpers moved to pty-support.ts.
export { inheritedEnv, buildPromptBytes, parseUserQuestions };

export class PtySession {
  // The current turn's event stream. Null between turns (process parked) — the
  // process outlives any single turn, so the queue is per-turn, not per-process.
  private activeQueue: AsyncEventQueue<HarnessEvent> | null = null;
  // The drain generator for the current turn, captured at turn-start and bound
  // to that turn's queue instance. events() returns THIS, not a fresh drain of
  // activeQueue — a turn can end (closing + nulling activeQueue) synchronously
  // within beginPassiveTurn (a buffered result) before the consumer calls
  // events(), and the consumer must still drain the already-buffered records.
  private turnStream: AsyncGenerator<HarnessEvent, void> | null = null;
  // Transcript events that arrive with no turn draining (parked window). A
  // passive turn replays these; beginTurn drops them.
  private betweenTurnBuffer: HarnessEvent[] = [];
  // Leading-edge guard so a burst of parked activity fires onPassiveActivity
  // once, not per event. Reset when a turn (re)starts.
  private passiveSignaled = false;
  private passiveListener: (() => void) | null = null;
  // Set once the CLI process actually exits (distinct from _toreDown, which is
  // our own teardown). Blocks reuse of a dead process.
  private exited = false;
  // Whether the turn that just ended did so via a clean transcript `result`
  // (eligible for parking) vs. an abort/error (must teardown).
  private lastTurnCleanResult = false;
  private socket: HookSocketServer | null = null;
  private tailer: JsonlTailer | null = null;
  // opencode structured-events state: the sink-record interpreter, and the
  // opencode-assigned session id it latched (reported via a synthetic init and
  // used for `--session` resume — opencode manages its own lineage, unlike
  // Claude's deterministic transcript UUIDs).
  private opencodeSource: OpenCodeEventSource | null = null;
  private reportedOpenCodeId: string | null = null;
  private pty: PtyProcess | null = null;
  private tempDir = "";
  private sawResult = false;
  private limitBannerReported = false;
  // Rolling tail of raw PTY output, retained only to enrich the error when the
  // CLI exits before emitting a result (the bytes are otherwise relayed to S5
  // and never become events). Trimmed to MAX_DIAGNOSTIC_OUTPUT on every write.
  private recentOutput = "";
  private coalescer: PtyOutputCoalescer | null = null;
  private _toreDown = false;
  // Raw-TUI input detection (see awaitRawTuiInputLive). `probeWindow`, when
  // non-null, accumulates raw output so a probe can look for its own sentinel
  // coming back.
  private spawnedAt = 0;
  // Set once the child writes a DEC private mode — it owns the tty from then on,
  // so the kernel no longer echoes our keystrokes and a sentinel coming back is
  // attributable to the app's own repaint.
  private sawTerminalSetup = false;
  private probeWindow: string | null = null;
  // Whether this process has already taken a prompt write. Only the FIRST one
  // waits on readiness: once the TUI is up, later writes (a multi-message
  // prompt, a follow-up turn) must go straight through — output is flowing
  // while the model works, so a quiet check would block until the turn ended.
  private wroteToProcess = false;
  private readonly exitListeners: ((code: number) => void)[] = [];
  private readonly idleListeners: (() => void)[] = [];
  private abortHandler: (() => void) | null = null;
  private cols = 120;
  private rows = 40;
  private unsubInput: (() => void) | null = null;
  private unsubResize: (() => void) | null = null;
  // In-process HTTP MCP servers exposing the harness tools to the spawned CLI,
  // plus the path to the `--mcp-config` that points at them.
  private toolServers: PtyToolServer[] = [];
  private mcpConfigPath: string | null = null;
  // Plan-dialog auto-accept state (see armPlanDialogAutoAccept).
  private pendingPlanApproval = false;
  private planApprovalTimer: NodeJS.Timeout | null = null;
  // Submit-nudge state (see armSubmitNudge).
  private pendingSubmitNudge = false;
  private submitNudgeTimer: NodeJS.Timeout | null = null;
  // Set when the nudge window expired with the turn still unstarted — the
  // pasted prompt is parked in the input box and no Enter will ever land. The
  // harness reads it after the turn's stream completes to respawn + redeliver.
  private _promptDeliveryFailed = false;
  // Text actually written into the TUI input this turn, retained so a respawn
  // can redeliver the identical prompt (the caller's prompt may be a
  // single-yield async generator that is already exhausted by then).
  private deliveredTexts: string[] = [];
  // Synthetic AskUserQuestion chat-card state. The CLI does NOT flush the
  // assistant record holding a pending AskUserQuestion tool_use to the
  // transcript until the questionnaire resolves (verified live on CLI 2.1.209:
  // dialog parked on screen, transcript untouched) — so a transcript-derived
  // question card could only ever render AFTER the human answered in the raw
  // terminal. Instead the PreToolUse hook (which fires at ask time and carries
  // the full questions input) emits a synthetic `tool_use` chat event under an
  // `aq-…` id. When the real records eventually flush, the duplicate tool_use
  // is dropped (FIFO match below) and its tool_result is re-pointed at the
  // synthetic id so the card flips to answered.
  private pendingSyntheticQuestionIds: string[] = [];
  private questionResultRemap = new Map<string, string>();

  // Per-turn state: the prompt to feed and the per-turn options subset. Both
  // start from the constructor args (turn 1) and are replaced by beginTurn.
  private turnPrompt: string | AsyncGenerator<HarnessUserMessage, void, unknown>;
  private turn: TurnOptions;

  constructor(
    prompt: string | AsyncGenerator<HarnessUserMessage, void, unknown>,
    // Spawn-level options — fixed for the life of the CLI process. Per-turn
    // fields (canUseTool/promptDelivery/planDialogAutoAccept/abortController)
    // are snapshotted into `this.turn` and overridden each turn.
    private readonly options: HarnessQueryOptions,
    private readonly resume?: string,
    private readonly bridge?: PtyBridge,
    private readonly adapter: TuiAdapter = new ClaudeTuiAdapter(),
  ) {
    this.turnPrompt = prompt;
    this.turn = turnOptionsFrom(options);
  }

  onIdle(listener: () => void): void {
    this.idleListeners.push(listener);
  }

  onExit(listener: (code: number) => void): void {
    this.exitListeners.push(listener);
  }

  /** Subscribe to parked-window transcript activity (human typed into the idle
   *  TUI). Returns unsubscribe. Only one listener is retained. */
  onPassiveActivity(listener: () => void): () => void {
    this.passiveListener = listener;
    return () => {
      if (this.passiveListener === listener) this.passiveListener = null;
    };
  }

  get isToreDown(): boolean {
    return this._toreDown;
  }

  /** True once the underlying CLI process has exited. */
  get hasExited(): boolean {
    return this.exited;
  }

  /** True when the last turn ended via a clean transcript result (parkable). */
  get endedCleanly(): boolean {
    return this.lastTurnCleanResult;
  }

  /**
   * True when this turn's prompt was pasted but never submitted — the nudge
   * window expired with no transcript record proving the turn started. The
   * harness treats it as a delivery failure and respawns + redelivers.
   */
  get promptDeliveryFailed(): boolean {
    return this._promptDeliveryFailed;
  }

  /** The exact text pasted into the TUI this turn — the payload a respawn
   *  redelivers. Empty when nothing was delivered. */
  get deliveredPromptText(): string {
    return this.deliveredTexts.join("\n\n");
  }

  /**
   * The tail of the raw terminal screen, ANSI-stripped and secret-scrubbed —
   * DIAGNOSTICS ONLY. The harness logs it when a prompt delivery fails, which
   * is the only record of what the TUI was actually parked on (a startup
   * dialog, a mount error, a login screen). Without it every park is
   * undiagnosable after the fact. This does not weaken the parity rule that raw
   * stdout is never parsed into events: nothing here becomes a HarnessEvent.
   */
  get diagnosticFrame(): string {
    const tail = cleanTerminalOutput(this.recentOutput, MAX_DIAGNOSTIC_OUTPUT);
    return tail ? redact(tail).output : "";
  }

  /**
   * The session identity this process is bound to. For Claude, the
   * deterministic lineage UUID; for opencode, the `ses_…` id the engine
   * assigned (latched from the plugin events), which wins once known so a
   * respawn's explicit resume target matches.
   */
  get sessionUuid(): string | undefined {
    return this.reportedOpenCodeId ?? this.resume ?? this.options.sessionId;
  }

  /** The opencode-assigned session id, or null (always null for Claude). */
  get reportedSessionId(): string | null {
    return this.reportedOpenCodeId;
  }

  /** Fingerprint of the spawn-time options a reused process cannot change. */
  get spawnFingerprint(): string {
    return this.adapter.spawnFingerprint({
      model: this.options.model,
      permissionMode: this.options.permissionMode,
      ...(this.options.appendSystemPrompt
        ? { appendSystemPrompt: this.options.appendSystemPrompt }
        : {}),
      cwd: this.options.cwd,
    });
  }

  /**
   * Whether this parked process can serve a turn wanting `resume`/`fingerprint`.
   * Requires: live (not torn down / not exited), idle (no turn draining), a
   * matching resume target, and matching spawn options.
   *
   * The resume match is adapter-shaped. Claude's resume tokens are transcript
   * files, so an undefined `resume` means "start a fresh conversation" and
   * must NOT reuse a parked process bound to an old one. opencode manages its
   * own lineage — the executor can never learn its `ses_…` ids from disk, so
   * every follow-up arrives with `resume` undefined; there, the live parked
   * process IS the conversation, and reuse is keyed on the id the plugin
   * events latched. Gating on `reportedOpenCodeId` (which Claude sessions
   * never set) keeps the Claude semantics byte-identical.
   */
  canReuse(resume: string | undefined, fingerprint: string): boolean {
    const resumeMatches =
      resume === undefined ? this.reportedOpenCodeId !== null : resume === this.sessionUuid;
    return (
      !this._toreDown &&
      !this.exited &&
      this.activeQueue === null &&
      resumeMatches &&
      fingerprint === this.spawnFingerprint
    );
  }

  get hookSocketPath(): string | null {
    return this.socket ? this.socket.socketPath : null;
  }

  events(): AsyncGenerator<HarnessEvent, void> {
    // Return the stream captured at the current turn's start. It is always set
    // by start()/beginTurn()/beginPassiveTurn() before the consumer calls this;
    // guard defensively so a stray call yields an empty, completed stream.
    if (!this.turnStream) {
      const empty = new AsyncEventQueue<HarnessEvent>();
      empty.close();
      return empty.drain();
    }
    return this.turnStream;
  }

  /**
   * Route a harness event to the current turn's queue, or — when no turn is
   * draining (the process is parked) — into the between-turn buffer, firing the
   * passive-activity signal once per parked window.
   */
  private pushEvent(event: HarnessEvent): void {
    if (this.activeQueue) {
      this.activeQueue.push(event);
      return;
    }
    this.betweenTurnBuffer.push(event);
    if (this.betweenTurnBuffer.length > MAX_BETWEEN_TURN_BUFFER) {
      this.betweenTurnBuffer.shift();
    }
    if (!this.passiveSignaled) {
      this.passiveSignaled = true;
      this.passiveListener?.();
    }
  }

  /**
   * Begin a follow-up turn on the ALREADY-RUNNING process: fresh per-turn state
   * and queue, rewire the abort listener to this turn's controller, then feed
   * the prompt into the live pty. The caller must have verified canReuse().
   */
  async beginTurn(
    prompt: string | AsyncGenerator<HarnessUserMessage, void, unknown>,
    options: HarnessQueryOptions,
  ): Promise<void> {
    this.turnPrompt = prompt;
    this.turn = turnOptionsFrom(options);
    this.resetForTurn();
    // A stale buffered `result` from the parked window must never terminate this
    // fresh turn, so drop the buffer (a passive turn replays it instead).
    this.betweenTurnBuffer = [];
    this.activeQueue = new AsyncEventQueue<HarnessEvent>();
    this.turnStream = this.activeQueue.drain();
    if (!this.rearmAbort()) return;
    await this.feedPrompt();
  }

  /**
   * Begin a PASSIVE turn: a human typed into the parked TUI and the CLI is
   * producing transcript records with no prompt from us. Replay the buffered
   * records into a fresh queue (a buffered `result` ends the turn at once) and
   * let live records continue to flow.
   */
  beginPassiveTurn(turn: TurnOptions): void {
    this.turn = turn;
    this.resetForTurn();
    const buffered = this.betweenTurnBuffer;
    this.betweenTurnBuffer = [];
    this.activeQueue = new AsyncEventQueue<HarnessEvent>();
    // Capture the stream before replaying: a buffered `result` ends the turn
    // (closing + nulling activeQueue) synchronously below, but the consumer must
    // still drain the records we just pushed.
    this.turnStream = this.activeQueue.drain();
    if (!this.rearmAbort()) return;
    for (const event of buffered) {
      this.activeQueue?.push(event);
      if (event.type === "result") this.endTurn(true);
    }
  }

  /** Reset per-turn state shared by beginTurn/beginPassiveTurn. */
  private resetForTurn(): void {
    this.sawResult = false;
    this.lastTurnCleanResult = false;
    this.passiveSignaled = false;
    this._promptDeliveryFailed = false;
    this.deliveredTexts = [];
    this.disarmSubmitNudge();
    this.disarmPlanDialogAutoAccept();
    // Backstop for a dialog dismissed without any transcript trace (endTurn
    // covers the clean-result path): a fresh turn means the old dialog is gone.
    this.closeSyntheticQuestionCards();
  }

  /**
   * (Re)register the abort→teardown listener on the current turn's controller,
   * removing any prior one. Returns false (after tearing down) when the signal
   * is already aborted, so callers can bail.
   */
  private rearmAbort(): boolean {
    if (this.abortHandler && this.turn.abortController) {
      this.turn.abortController.signal.removeEventListener("abort", this.abortHandler);
    }
    this.abortHandler = null;
    const signal = this.turn.abortController?.signal;
    if (!signal) return true;
    if (signal.aborted) {
      void this.teardown();
      return false;
    }
    this.abortHandler = () => {
      void this.teardown();
    };
    signal.addEventListener("abort", this.abortHandler, { once: true });
    return true;
  }

  /**
   * End the current turn WITHOUT killing the process: close+null the queue so
   * the consumer's generator completes, and unhook the (now-finished) turn's
   * abort listener so a later abort of that controller can't teardown a process
   * we intend to reuse. The process stays alive (parked) for the next turn.
   */
  private endTurn(clean: boolean): void {
    this.lastTurnCleanResult = clean;
    // An answered questionnaire drained this state before the turn's result
    // record (transcript order); anything left is an interrupted dialog whose
    // records will never flush — close its card.
    this.closeSyntheticQuestionCards();
    this.activeQueue?.close();
    this.activeQueue = null;
    if (this.abortHandler && this.turn.abortController) {
      this.turn.abortController.signal.removeEventListener("abort", this.abortHandler);
      this.abortHandler = null;
    }
  }

  async start(): Promise<void> {
    // Fail fast before allocating resources: the tailer can only locate the
    // transcript if we know which session id the CLI will write to.
    const sessionId = this.resume ?? this.options.sessionId;
    if (!sessionId) {
      throw new Error("PtySession requires options.sessionId or a resume target");
    }
    // This is turn 1: allocate its queue up front so the abort-before-alloc
    // teardown below has a queue to close (a parked consumer completes).
    this.activeQueue = new AsyncEventQueue<HarnessEvent>();
    this.turnStream = this.activeQueue.drain();
    // stop()/softStop() abort the query's shared AbortController. The SDK
    // harness consumes it natively; under the PTY harness we must translate an
    // abort into teardown() — otherwise a mid-turn `claude` (which may never
    // emit a transcript `result` and does not exit) is left running while the
    // event consumer stays parked on the queue.
    const signal = this.turn.abortController?.signal;
    if (signal?.aborted) {
      // Already aborted before we allocated anything — tear down (which closes
      // the empty queue so a parked consumer completes) and bail.
      await this.teardown();
      return;
    }
    try {
      if (this.adapter.capabilities.structuredEvents && this.adapter.id === "opencode") {
        // Trusted structured-event source, opencode flavor: the Conveyor
        // events plugin appends the client engine's bus to an NDJSON sink,
        // tailed like a transcript. Tool servers are shared with the Claude
        // path; the config rides the child env instead of files/sockets.
        const extras = await this.startOpenCodeEventSources();
        await this.spawn(undefined, undefined, extras);
      } else if (this.adapter.capabilities.structuredEvents) {
        // Trusted structured-event source (transcript tailer + hook socket +
        // tool servers): lifecycle status derives from transcript records, not
        // process aliveness. Allocate it, then spawn wired to those resources.
        const { settingsPath, socketPath } = await this.startStructuredEventSources(sessionId);
        await this.spawn(settingsPath, socketPath);
      } else {
        // Raw terminal mode: the TUI exposes no trusted event source, so we run
        // it as a plain relayed terminal — no hook socket, settings, tool
        // servers, or tailer. Lifecycle status derives from process aliveness:
        // a synthetic init on spawn here, and a result on exit in
        // finalizeOnExit. A tempDir is still allocated for teardown symmetry.
        this.tempDir = await mkdtemp(join(sessionTempBase(), "conveyor-pty-"));
        await this.spawn();
        this.pushEvent({
          type: "system",
          subtype: "init",
          session_id: sessionId,
          model: this.options.model,
        });
      }
      // Register after spawn so the handler has a live pty to kill, then
      // re-check: an abort racing with allocation (fired before the listener
      // attached) would otherwise be missed.
      if (signal) {
        this.abortHandler = () => {
          void this.teardown();
        };
        signal.addEventListener("abort", this.abortHandler, { once: true });
        if (signal.aborted) {
          await this.teardown();
          return;
        }
      }
      // Wire relayed keystrokes / resizes into the live pty. Registered only
      // after a successful spawn so writeStdin/resizePty have a process to
      // target; the unsubscribers are cleared in teardown().
      if (this.bridge) {
        this.unsubInput = this.bridge.onInput((data) => this.writeStdin(data));
        this.unsubResize = this.bridge.onResize((cols, rows) => this.resizePty(cols, rows));
      }
      await this.feedPrompt();
    } catch (err) {
      // A failure after we begin allocating (socket listen, tailer interval,
      // temp dir, spawn) would otherwise leak those resources: the PtyHarness
      // consumer only calls teardown() once start() has resolved.
      await this.teardown();
      throw err;
    }
  }

  /**
   * Allocate the opencode structured-event sources: the in-process tool
   * servers (shared with the Claude path — the same loopback StreamableHTTP
   * servers, consumed via config `mcp` entries instead of `--mcp-config`),
   * the instructions file carrying the system prompt, the staged Conveyor
   * events plugin, and a tailer on its sink. Returns the spawn extras the
   * adapter bakes into OPENCODE_CONFIG_CONTENT.
   *
   * The tailer routes every parsed record to the OpenCodeEventSource, which
   * owns per-turn state (roles, usage, busy/idle) and feeds the mapped events
   * through the SAME handleTranscriptEvent path Claude records take — so
   * nudge-disarm, passive buffering, and result-ends-turn run unchanged. The
   * synthetic `system init` reports the opencode-assigned session id the
   * moment it is known, which is how the runner learns the resume target.
   */
  private async startOpenCodeEventSources(): Promise<{
    mcpEntries: Record<string, import("./tool-server.js").McpConfigEntry>;
    eventsSinkPath: string;
    pluginPath: string;
    instructionsPath?: string;
  }> {
    this.tempDir = await mkdtemp(join(sessionTempBase(), "conveyor-pty-"));
    const { servers, entries } = await startToolServers(
      this.options.mcpServers ?? {},
      this.tempDir,
    );
    this.toolServers = servers;
    const { pluginPath, eventsSinkPath } = await stageOpenCodePlugin(this.tempDir);
    let instructionsPath: string | undefined;
    const systemPrompt = this.options.appendSystemPrompt;
    if (systemPrompt && systemPrompt.trim() !== "") {
      instructionsPath = join(this.tempDir, "conveyor-instructions.md");
      await writeFile(instructionsPath, systemPrompt, "utf8");
    }
    this.opencodeSource = new OpenCodeEventSource(
      (event) => this.handleTranscriptEvent(event),
      (id) => {
        this.reportedOpenCodeId = id;
        this.pushEvent({
          type: "system",
          subtype: "init",
          session_id: id,
          model: this.options.model,
        });
        this.sendChatEvent({ kind: "init", model: this.options.model, claudeSessionId: id });
      },
      (event) => this.sendChatEvent(event),
    );
    this.tailer = new JsonlTailer(
      eventsSinkPath,
      () => undefined,
      (raw) => this.opencodeSource?.handleRecord(raw),
      () => null,
    );
    this.tailer.start(0);
    return {
      mcpEntries: entries,
      eventsSinkPath,
      pluginPath,
      ...(instructionsPath ? { instructionsPath } : {}),
    };
  }

  /**
   * Allocate the Claude-style structured-event sources: the PostToolUse hook
   * socket, the per-run settings file, the in-process tool servers, and the
   * transcript tailer. Only structured-events adapters call this — raw-terminal
   * adapters have no trusted event source and skip it entirely. Returns the
   * paths spawn() must wire into the child's argv/env.
   */
  private async startStructuredEventSources(
    sessionId: string,
  ): Promise<{ settingsPath: string; socketPath: string }> {
    this.tempDir = await mkdtemp(join(sessionTempBase(), "conveyor-pty-"));
    const socketPath = join(this.tempDir, "hook.sock");
    this.socket = new HookSocketServer(
      socketPath,
      (progress) => this.handleProgress(progress),
      (request) => this.handlePreToolUse(request),
    );
    await this.socket.listen();
    // Plan-permission spawns prompt for any tool the CLI cannot prove is
    // read-only, and a card agent has nobody at the terminal to answer — so
    // Conveyor answers for EVERY tool there. Build-capable spawns bypass
    // prompts entirely and keep the scoped matcher list.
    const { settingsPath } = await writeHookSettings(this.tempDir, {
      gateAllTools: this.options.permissionMode === "plan",
    });
    // Expose the harness's in-process tools to the spawned CLI over a loopback
    // HTTP MCP server, written into a `--mcp-config` consumed by spawn().
    await this.setupToolServers();
    const transcriptPath = sessionTranscriptPath(this.options.cwd, sessionId);
    await mkdir(dirname(transcriptPath), { recursive: true });
    // On resume the transcript already holds the prior session's records; start
    // tailing at EOF so historical records aren't replayed as fresh events.
    const startOffset = this.resume ? await transcriptSize(transcriptPath) : 0;
    // Chat-proxy relay: raw records (including user prompts, which the
    // harness mapper drops) are projected to compact chat events at the
    // tailer level, so between-turn/parked records relay too. The same raw
    // feed carries the background-task completion notifications — also a user
    // record the harness mapper drops.
    const wantsRawRecords =
      typeof this.bridge?.sendChatEvent === "function" ||
      typeof this.bridge?.notifyBackgroundTaskDone === "function";
    this.tailer = new JsonlTailer(
      transcriptPath,
      (event) => this.handleTranscriptEvent(event),
      wantsRawRecords ? (raw) => this.handleRawRecord(raw) : undefined,
    );
    this.tailer.start(startOffset);
    return { settingsPath, socketPath };
  }

  /**
   * Fan one tailed transcript record out to its raw-record consumers: the
   * background-work signal (a `<task-notification>` user turn means a
   * backgrounded gate/subagent just finished) and the chat-proxy projection.
   */
  private handleRawRecord(raw: unknown): void {
    if (isBackgroundTaskNotificationRecord(raw)) this.bridge?.notifyBackgroundTaskDone?.();
    if (typeof this.bridge?.sendChatEvent === "function") this.relayChatRecord(raw);
  }

  /**
   * Project a tailed transcript record to chat events, reconciling them with
   * any synthetic question card already emitted at hook time: the flushed
   * AskUserQuestion `tool_use` duplicate is dropped (its real id remembered),
   * and the paired `tool_result` is re-pointed at the synthetic id so the
   * live-rendered card is the one that flips to answered.
   */
  private relayChatRecord(raw: unknown): void {
    for (const event of mapChatRecords(raw)) {
      if (event.kind === "tool_use" && event.name === "AskUserQuestion") {
        const syntheticId = this.pendingSyntheticQuestionIds.shift();
        if (syntheticId) {
          if (event.id) this.questionResultRemap.set(event.id, syntheticId);
          continue;
        }
      } else if (event.kind === "tool_result" && event.toolUseId) {
        const syntheticId = this.questionResultRemap.get(event.toolUseId);
        if (syntheticId) {
          this.questionResultRemap.delete(event.toolUseId);
          this.sendChatEvent({ ...event, toolUseId: syntheticId });
          continue;
        }
      }
      this.sendChatEvent(event);
    }
  }

  private sendChatEvent(event: PtyChatEventPayload): void {
    this.bridge?.sendChatEvent?.(event);
  }

  /** Render the question card in the web chat NOW — at hook time — instead of
   * whenever the CLI flushes the transcript records (which is only after the
   * questionnaire resolves; see the field comment). */
  private emitSyntheticQuestionCard(questions: HarnessUserQuestion[]): void {
    if (questions.length === 0 || typeof this.bridge?.sendChatEvent !== "function") return;
    const id = `aq-${randomUUID()}`;
    this.pendingSyntheticQuestionIds.push(id);
    this.sendChatEvent({
      kind: "tool_use",
      name: "AskUserQuestion",
      input: compactQuestionsJson(questions),
      id,
    });
  }

  /**
   * Close any still-open synthetic question cards. The questionnaire can only
   * outlive its card via a path that never flushes the paired records — Esc /
   * interrupt, a superseding turn, or process teardown — so an answering
   * tool_result will never arrive for these ids; emit one so the web card
   * stops soliciting input for a dialog that no longer exists.
   */
  private closeSyntheticQuestionCards(): void {
    const orphaned = [...this.pendingSyntheticQuestionIds, ...this.questionResultRemap.values()];
    this.pendingSyntheticQuestionIds = [];
    this.questionResultRemap.clear();
    for (const toolUseId of orphaned) {
      this.sendChatEvent({ kind: "tool_result", toolUseId, output: "", isError: false });
    }
  }

  writeStdin(text: string): void {
    this.pty?.write(text);
  }

  /**
   * Inject a follow-up message into the CURRENTLY-RUNNING turn by pasting it into
   * the live TUI input, exactly as a human typing mid-turn would — the CLI queues
   * it and picks it up at the next turn boundary. Unlike beginTurn this does NOT
   * reset per-turn state, allocate a new queue, or re-arm the abort listener: the
   * in-flight turn (and its transcript stream) keeps flowing, so the injected
   * message and its response ride the SAME event pipeline. Returns false when
   * there is no live, actively-draining turn to inject into — parked/idle
   * (activeQueue === null), torn down, exited, or a raw-terminal TUI that gives
   * us no trusted signal that a turn is running — so the caller falls back to the
   * abort+respawn supersede path.
   */
  injectIntoRunningTurn(text: string): boolean {
    if (!this.pty || this._toreDown || this.exited) return false;
    // A null activeQueue means the process is parked/idle (no turn draining):
    // there is nothing to inject into, so a fresh turn (feedPrompt via beginTurn)
    // is the correct path, not a mid-turn paste.
    if (this.activeQueue === null) return false;
    // Only trusted structured-event TUIs (claude) — a raw terminal exposes no
    // way to know a turn is actually running, so injecting could race a parked
    // process we misread as busy.
    if (!this.adapter.capabilities.structuredEvents) return false;
    if (!text.trim()) return false;
    void this.submitLivePrompt(text);
    return true;
  }

  /**
   * Paste + submit a prompt into the live pty WITHOUT any turn bookkeeping.
   * Mirrors deliverPrompt's submit path (bracketed paste, then a separate Enter
   * after a settle window) but never arms the submit nudge — the running turn is
   * already producing transcript records, so re-pressing Enter would risk
   * accepting an unrelated mid-turn dialog. Fire-and-forget.
   */
  private async submitLivePrompt(text: string): Promise<void> {
    this.writeStdin(this.adapter.encodePromptBytes(text));
    await sleep(resolveSubmitSettleMs());
    if (this._toreDown || this.exited) return;
    this.writeStdin("\r");
  }

  /** Apply a relayed resize to the live pty (reconciled dims from the server). */
  private resizePty(cols: number, rows: number): void {
    if (cols <= 0 || rows <= 0) return;
    this.cols = cols;
    this.rows = rows;
    try {
      this.pty?.resize(cols, rows);
    } catch {
      /* pty already exited */
    }
  }

  /**
   * Force the CLI to repaint its full screen by wiggling the pty size
   * (SIGWINCH). Used after a socket reconnect: the server's scrollback ring is
   * per-process and starts empty on a new instance, so without a repaint a
   * quiet TUI would leave the Connected-TUI terminal blank (and hidden) until
   * the CLI next draws on its own.
   */
  forceRepaint(): void {
    if (!this.pty) return;
    try {
      this.pty.resize(this.cols, Math.max(1, this.rows - 1));
      this.pty.resize(this.cols, this.rows);
    } catch {
      /* pty already exited */
    }
  }

  async teardown(): Promise<void> {
    if (this._toreDown) return;
    this._toreDown = true;
    this.disarmPlanDialogAutoAccept();
    this.disarmSubmitNudge();
    // A dialog parked at kill time will never resolve — close its web card.
    this.closeSyntheticQuestionCards();
    this.unsubInput?.();
    this.unsubInput = null;
    this.unsubResize?.();
    this.unsubResize = null;
    if (this.abortHandler) {
      this.turn.abortController?.signal.removeEventListener("abort", this.abortHandler);
      this.abortHandler = null;
    }
    const pty = this.pty;
    this.pty = null;
    if (pty) killPtyWithEscalation(pty, () => this.exited);
    this.coalescer?.dispose();
    this.coalescer = null;
    this.tailer?.close();
    this.tailer = null;
    if (this.socket) {
      await this.socket.close();
      this.socket = null;
    }
    for (const toolServer of this.toolServers) {
      try {
        await toolServer.close();
      } catch {
        /* already closed */
      }
    }
    this.toolServers = [];
    this.mcpConfigPath = null;
    this.activeQueue?.close();
    this.activeQueue = null;
    if (this.tempDir) {
      await rm(this.tempDir, { recursive: true, force: true });
      this.tempDir = "";
    }
  }

  /**
   * Serve the harness's in-process tools to the spawned CLI over loopback HTTP
   * (handlers run in THIS process against the live task-token connection) and
   * record the `--mcp-config` path for spawn(). No-op when the harness was
   * constructed without tools (e.g. SDK-only callers).
   */
  private async setupToolServers(): Promise<void> {
    const { servers, mcpConfigPath } = await startToolServers(
      this.options.mcpServers ?? {},
      this.tempDir,
    );
    this.toolServers = servers;
    this.mcpConfigPath = mcpConfigPath;
  }

  private async spawn(
    settingsPath?: string,
    socketPath?: string,
    opencodeExtras?: {
      mcpEntries: Record<string, import("./tool-server.js").McpConfigEntry>;
      eventsSinkPath: string;
      pluginPath: string;
      instructionsPath?: string;
    },
  ): Promise<void> {
    // An opencode respawn resumes the engine-assigned id when one was latched
    // (the constructor `resume` is a Claude-lineage UUID opencode cannot know).
    const resume = this.reportedOpenCodeId ?? this.resume;
    const spec = this.adapter.buildSpawn({
      options: this.options,
      ...(resume ? { resume } : {}),
      ...(settingsPath ? { settingsPath } : {}),
      ...(socketPath ? { hookSocketPath: socketPath } : {}),
      // Only this config: ignore the user's ~/.claude.json / project .mcp.json
      // so the agent's tool set is deterministic (and a stale personal conveyor
      // server doesn't load).
      ...(this.mcpConfigPath ? { mcpConfigPath: this.mcpConfigPath } : {}),
      ...(opencodeExtras ?? {}),
    });
    const spawn = await resolvePtySpawn();
    const pty = spawn(spec.file, spec.args, {
      name: "xterm-color",
      cols: this.cols,
      rows: this.rows,
      cwd: this.options.cwd,
      env: spec.env,
    });
    // Mirror raw terminal output to the relay (and on to the S5 terminal). The
    // first flush creates the server-side scrollback ring — which is what makes
    // the Connected-TUI terminal appear, i.e. this doubles as the attach
    // handshake. Output is coalesced (~40ms windows) so a busy redraw becomes
    // one relay call instead of tens — per-frame server cost was starving the
    // stream. Stdout is intentionally NOT turned into HarnessEvents.
    const bridge = this.bridge;
    if (bridge) {
      this.coalescer = new PtyOutputCoalescer(
        (data, dims) => {
          bridge.sendOutput(data, dims);
        },
        () => ({ cols: this.cols, rows: this.rows }),
      );
    }
    pty.onData((data) => {
      this.coalescer?.write(data);
      // Raw-TUI input detection: an open probe window collects output so a probe
      // can confirm the TUI rendered its sentinel back.
      if (!this.sawTerminalSetup && detectTerminalSetup(data)) this.sawTerminalSetup = true;
      if (this.probeWindow !== null) this.probeWindow += data;
      // Retain a bounded tail so finalizeOnExit can explain an early exit.
      this.recentOutput = (this.recentOutput + data).slice(-MAX_DIAGNOSTIC_OUTPUT);
    });
    pty.onExit((event) => {
      void this.finalizeOnExit(event.exitCode);
    });
    this.spawnedAt = Date.now();
    this.sawTerminalSetup = false;
    this.probeWindow = null;
    // A respawn (submit-redelivery) faces a cold TUI again, so the next write
    // must re-wait rather than inherit the previous process's readiness.
    this.wroteToProcess = false;
    this.pty = pty;
  }

  /** Open a fresh probe-observation window over raw output. */
  private openProbeWindow(): void {
    this.probeWindow = "";
  }

  /**
   * Write `bytes`, then wait up to `ackMs` for the TUI to render `sentinel` back.
   * Positive proof that a live input box consumed the keystrokes.
   */
  private async writeAndAwaitEcho(
    bytes: string,
    sentinel: string,
    timing: { ackMs: number; pollMs: number },
  ): Promise<boolean> {
    this.openProbeWindow();
    this.writeStdin(bytes);
    const deadline = Date.now() + timing.ackMs;
    while (Date.now() < deadline) {
      if (this._toreDown) return false;
      if (sentinelEchoed(this.probeWindow ?? "", sentinel)) return true;
      await sleep(timing.pollMs);
    }
    return false;
  }

  /**
   * Wait until a raw-relay TUI's input box is DEMONSTRABLY accepting keystrokes,
   * by typing a short sentinel and watching for the TUI to render it back, then
   * erasing it. Readiness is detected, never inferred from elapsed time.
   *
   * A structured-events TUI does not need this: `armSubmitNudge` re-presses
   * Enter until a transcript record proves the turn started. A raw adapter has
   * no such evidence, so the nudge is deliberately never armed for it — its
   * single paste + Enter is the only shot, and one fired during startup is
   * discarded with no trace, leaving the card parked on an empty input box.
   *
   * See resolveRawTuiProbeTiming for the measurements behind this, including the
   * signals that were tried and rejected (DECSET 2004, output-quiet windows, and
   * "any output after a keystroke" — all of which report ready too early).
   */
  private async awaitRawTuiInputLive(): Promise<void> {
    if (!needsRawReadyGate(this.adapter.capabilities)) return;
    const timing = resolveRawTuiProbeTiming();
    const { sentinel } = timing;
    const start = this.spawnedAt || Date.now();

    // 1. Wait for the child to take over the terminal. Before that the tty is
    //    still in canonical mode and the kernel echoes our own keystrokes back,
    //    which is indistinguishable from a repaint — probing there reports live
    //    instantly and the prompt is then pasted into a dead input.
    const setupDeadline = start + timing.firstOutputMaxMs;
    while (!this._toreDown && Date.now() < setupDeadline) {
      if (this.sawTerminalSetup) break;
      await sleep(timing.pollMs);
    }
    if (this._toreDown) return;

    // 2. Probe until the box echoes the sentinel. A probe that lands before the
    //    input handler is live is discarded silently, leaving nothing behind —
    //    which is exactly why retrying is safe.
    const deadline = start + timing.maxMs;
    let live = false;
    let probes = 0;
    while (!this._toreDown && Date.now() < deadline) {
      probes++;
      if (await this.writeAndAwaitEcho(sentinel, sentinel, timing)) {
        live = true;
        break;
      }
      await sleep(timing.retryMs);
    }
    if (this._toreDown) return;

    if (!live) {
      // Cap reached. Paste anyway: a stalled turn whose prompt was never sent is
      // strictly worse to debug than one that failed loudly downstream.
      process.stderr.write(
        `[conveyor-agent] raw TUI never echoed the input probe after ${probes} attempt(s) ` +
          `in ${Date.now() - start}ms — pasting anyway\n`,
      );
      this.probeWindow = null;
      return;
    }

    // 3. Erase the sentinel. An unacknowledged probe leaves nothing, but a
    //    LATE-acknowledged one can have deposited more than one copy, so erase
    //    until the box no longer shows it.
    for (let round = 0; round < probes && !this._toreDown; round++) {
      const gone = !(await this.writeAndAwaitEcho(
        "\x7f".repeat(sentinel.length),
        sentinel,
        timing,
      ));
      if (gone) break;
    }
    this.probeWindow = null;
  }

  private async deliverPrompt(text: string): Promise<void> {
    // An empty prompt on a TUI that can't prefill has nothing to paste and no
    // parked input to leave editable — deliver nothing (a raw-mode wake just
    // spawns the terminal for the human).
    if (text === "" && !this.adapter.capabilities.prefill) return;
    if (!this.wroteToProcess) {
      await this.awaitRawTuiInputLive();
      if (this._toreDown) return;
      this.wroteToProcess = true;
    }
    this.writeStdin(this.adapter.encodePromptBytes(text));
    if (this.turn.promptDelivery === "prefill") return;
    // Retain the submitted text so a delivery failure can be recovered by
    // respawning and redelivering it verbatim. Prefill returns above: a parked
    // prefill is waiting on a human by design, never a failure.
    this.deliveredTexts.push(text);
    // The Enter goes in a separate write after a settle window — folded into
    // the same chunk as the paste, the CLI can treat it as part of the paste
    // burst and leave the prompt parked unsubmitted in the input box.
    await sleep(resolveSubmitSettleMs());
    if (this._toreDown) return;
    this.writeStdin("\r");
    // The submit nudge is disarmed by the first transcript record proving the
    // turn started; a raw-terminal TUI produces no such evidence, so arming it
    // would re-press Enter for the full window. Only arm with a trusted source.
    if (this.adapter.capabilities.structuredEvents) this.armSubmitNudge();
  }

  private async feedPrompt(): Promise<void> {
    if (typeof this.turnPrompt === "string") {
      await this.deliverPrompt(this.turnPrompt);
      return;
    }
    for await (const message of this.turnPrompt) {
      const content = message.message.content;
      const text = typeof content === "string" ? content : renderPromptContentText(content);
      await this.deliverPrompt(text);
    }
  }

  /**
   * Even as a separate write, the submitting Enter can race CLI startup and
   * be swallowed, leaving the pasted prompt parked in the input box with
   * nothing to un-park it (the 45s parked-TUI watchdog only reports status).
   * Until the first transcript record proves the turn started, re-press Enter
   * on a bounded interval. Enter on an empty or mid-turn input box is a no-op.
   * Deliberate trade-off (same as the plan-dialog auto-accept below): a press
   * can accept the default of an unexpected startup dialog — in a headless
   * pod that unblocks the run rather than parking it forever.
   *
   * Two phases: SUBMIT_NUDGE_MAX_PRESSES fast presses for the swallowed-Enter
   * race, then a slow cadence out to SUBMIT_NUDGE_WINDOW_MS for startup
   * dialogs that render late on cold pods (the folder-trust dialog's default
   * is the accept option, so one late Enter un-parks the run).
   *
   * Exhausting the window without a transcript record is NOT a shrug — Enter
   * cannot clear every startup dialog (one needing an arrow-key selection eats
   * every press), and an autonomous card has no human at the terminal and no
   * next message coming, so the old silent give-up parked it indefinitely. The
   * window now ends in an explicit delivery failure the harness recovers from.
   */
  private armSubmitNudge(): void {
    if (this.submitNudgeTimer) return;
    this.pendingSubmitNudge = true;
    const startedAt = Date.now();
    const { intervalMs, slowIntervalMs, maxPresses, windowMs } = resolveSubmitNudgeTiming();
    let presses = 0;
    const press = (): void => {
      if (this._toreDown || !this.pendingSubmitNudge) {
        this.disarmSubmitNudge();
        return;
      }
      if (Date.now() - startedAt >= windowMs) {
        this.disarmSubmitNudge();
        this.handleSubmitDeliveryFailure(presses, windowMs);
        return;
      }
      presses++;
      this.writeStdin("\r");
      if (presses === maxPresses && this.submitNudgeTimer) {
        clearInterval(this.submitNudgeTimer);
        this.submitNudgeTimer = setInterval(press, slowIntervalMs);
      }
    };
    this.submitNudgeTimer = setInterval(press, intervalMs);
  }

  /**
   * The nudge window expired with the prompt still parked in the input box.
   * Record it, log the presses-used telemetry (so fleet audits can measure how
   * often Enter is actually being swallowed), surface it on the card, and END
   * THE TURN so the harness's drain completes and can respawn + redeliver —
   * otherwise the consumer stays parked on a queue that will never produce an
   * event. `clean: false` routes the session to teardown, not to parking.
   */
  private handleSubmitDeliveryFailure(pressesUsed: number, windowMs: number): void {
    if (this._promptDeliveryFailed) return;
    this._promptDeliveryFailed = true;
    const seconds = Math.round(windowMs / 1000);
    process.stderr.write(
      `[PtySession] prompt submission failed — no transcript record after ${pressesUsed} Enter ` +
        `press(es) over ${seconds}s; the prompt is parked unsubmitted in the TUI input\n`,
    );
    this.bridge?.notifyPromptDeliveryFailure?.(
      `⚠️ The agent's prompt was pasted into the Claude TUI but never submitted — ` +
        `${pressesUsed} Enter press(es) over ${seconds}s produced no turn. ` +
        `Restarting the TUI and redelivering the prompt.`,
    );
    this.endTurn(false);
  }

  private disarmSubmitNudge(): void {
    this.pendingSubmitNudge = false;
    if (this.submitNudgeTimer) {
      clearInterval(this.submitNudgeTimer);
      this.submitNudgeTimer = null;
    }
  }

  private handleProgress(progress: ToolProgress): void {
    // The ExitPlanMode PostToolUse envelope is the proof the plan-approval
    // dialog was answered — the tool only executes after approval. It is the
    // one reliable stop signal for the auto-accept presses (see
    // armPlanDialogAutoAccept for why transcript records are NOT one).
    if (this.pendingPlanApproval && progress.tool_name === "ExitPlanMode") {
      this.disarmPlanDialogAutoAccept();
    }
    // A tool actually ran: the turn is unambiguously underway even if the
    // transcript records haven't flushed yet (the CLI batches them).
    if (this.pendingSubmitNudge) this.disarmSubmitNudge();
    this.pushEvent({
      type: "tool_progress",
      ...(progress.tool_name === undefined ? {} : { tool_name: progress.tool_name }),
      ...(progress.elapsed_time_seconds === undefined
        ? {}
        : { elapsed_time_seconds: progress.elapsed_time_seconds }),
    });
  }

  /**
   * PreToolUse request from the hook helper → the runner's `canUseTool`
   * verdict. ExitPlanMode is the Conveyor plan-exit gate (validation +
   * identification trigger run inside the callback); Bash is answered by
   * Conveyor's per-mode policy so plan mode never parks on the CLI approval
   * dialog; AskUserQuestion is observed and always allowed. Absent callback →
   * allow (mirrors the SDK default).
   */
  private async handlePreToolUse(request: PreToolUseRequest): Promise<PreToolUseVerdict> {
    // The model is calling a tool, so the turn started — earliest proof we get,
    // ahead of both the transcript flush and the PostToolUse envelope.
    if (this.pendingSubmitNudge) this.disarmSubmitNudge();
    if (request.tool_name === "AskUserQuestion") {
      // Observe-only: the TUI renders the questionnaire and collects the
      // answer natively. Never delegate to `canUseTool` — the SDK-era handler
      // there parks on a blocking RPC, which under PTY would time the hook
      // out and deny the questionnaire. Disarm both press loops first: a
      // pending Enter press would answer the dialog's default option.
      this.disarmSubmitNudge();
      this.disarmPlanDialogAutoAccept();
      const questions = parseUserQuestions(request.tool_input);
      this.pushEvent({ type: "user_question", questions });
      // The hook is the ONLY ask-time signal — the transcript record of this
      // tool_use doesn't flush until the questionnaire resolves — so the web
      // question card must be emitted here to render while it matters.
      this.emitSyntheticQuestionCard(questions);
      return { decision: "allow" };
    }
    const canUseTool = this.turn.canUseTool;
    let verdict: PreToolUseVerdict;
    if (canUseTool) {
      const result = await canUseTool(request.tool_name, request.tool_input);
      verdict =
        result.behavior === "allow"
          ? { decision: "allow" }
          : { decision: "deny", reason: result.message };
    } else {
      verdict = { decision: "allow" };
    }
    if (
      verdict.decision === "allow" &&
      request.tool_name === "ExitPlanMode" &&
      this.turn.planDialogAutoAccept
    ) {
      this.armPlanDialogAutoAccept();
    }
    return verdict;
  }

  /**
   * A hook "allow" does not bypass the CLI's plan-approval dialog — it renders
   * right after the verdict and parks the turn until answered. Press Enter
   * (default option approves) until the ExitPlanMode PostToolUse envelope
   * proves the dialog was answered (handleProgress), or the turn ends, over a
   * two-phase fast/slow window.
   *
   * Transcript records are deliberately NOT a stop signal: the CLI flushes the
   * assistant record holding the ExitPlanMode tool_use ~0.5s AFTER the
   * PreToolUse hook fires (verified live on 2.1.202), so when the Conveyor
   * verdict resolves quickly that record lands right after arming — a
   * record-based disarm then cancels the presses before the first one fires,
   * which is exactly the failure that parked auto cards on the approval
   * dialog until a human pressed Enter.
   */
  private armPlanDialogAutoAccept(): void {
    if (this.pendingPlanApproval) return;
    this.pendingPlanApproval = true;
    const startedAt = Date.now();
    const { firstPressMs, intervalMs, slowIntervalMs, fastWindowMs, windowMs } =
      resolvePlanDialogTiming();
    const press = (): void => {
      if (this._toreDown || !this.pendingPlanApproval) return;
      const elapsed = Date.now() - startedAt;
      if (elapsed >= windowMs) {
        // Give up, loudly: without the PostToolUse envelope the dialog is
        // (most likely) still parked — leave a trace for diagnosis.
        process.stderr.write(
          "[PtySession] plan-dialog auto-accept window expired without ExitPlanMode executing — the approval dialog may still be parked\n",
        );
        this.disarmPlanDialogAutoAccept();
        return;
      }
      this.writeStdin("\r");
      const interval = elapsed < fastWindowMs ? intervalMs : slowIntervalMs;
      this.planApprovalTimer = setTimeout(press, interval);
    };
    this.planApprovalTimer = setTimeout(press, firstPressMs);
  }

  private disarmPlanDialogAutoAccept(): void {
    this.pendingPlanApproval = false;
    if (this.planApprovalTimer) {
      clearTimeout(this.planApprovalTimer);
      this.planApprovalTimer = null;
    }
  }

  private handleTranscriptEvent(event: HarnessEvent): void {
    // End-of-turn means the dialog is moot (answered, or the CLI moved on).
    // The mid-turn stop signal is the ExitPlanMode PostToolUse envelope in
    // handleProgress — see armPlanDialogAutoAccept for why arbitrary
    // transcript records must not disarm.
    if (this.pendingPlanApproval && event.type === "result") {
      this.disarmPlanDialogAutoAccept();
    }
    // Model output proves the submitted prompt actually started a turn — the
    // submit nudge has done its job. Deliberately NOT "any transcript record":
    // a `--resume` spawn tails from EOF, so a `system` record the CLI writes at
    // boot lands after our start offset and would disarm the nudge while the
    // prompt is still parked unsubmitted — the exact false-positive that turns
    // a recoverable swallowed Enter into a silent indefinite park. Extra Enter
    // presses against an already-running turn are harmless (the input box is
    // empty), so erring toward a late disarm is the safe direction.
    if (this.pendingSubmitNudge && (event.type === "assistant" || event.type === "result")) {
      this.disarmSubmitNudge();
    }
    this.synthesizeRateLimitFromBanner(event);
    this.pushEvent(event);
    if (event.type === "result") {
      this.sawResult = true;
      for (const listener of this.idleListeners) listener();
      // A transcript `result` marks end-of-turn. End the turn (close the
      // stream so the consumer's generator completes — mirroring the SDK
      // harness's per-query model) but keep the process alive: interactive
      // `claude` does not exit after a result, so the harness parks it for the
      // next turn instead of tearing it down.
      this.endTurn(true);
    }
  }

  /**
   * The interactive CLI reports a hard usage cap only as a conversation banner
   * ("You've hit your weekly limit · resets 7am (UTC)") — it never writes a
   * structured rate_limit_event to the transcript. Recognize the banner in
   * assistant/result text and push the same harness-level event the SDK
   * harness emits, so cap handling (key cycle → pause) is one code path.
   * Once per session: the CLI repeats the banner on every rejected turn.
   */
  private synthesizeRateLimitFromBanner(event: HarnessEvent): void {
    if (this.limitBannerReported) return;
    let text: string | undefined;
    if (event.type === "assistant") {
      text = event.message.content
        .map((block) => block.text ?? "")
        .filter(Boolean)
        .join("\n");
    } else if (event.type === "result" && event.subtype === "success") {
      text = event.result;
    }
    if (!text) return;
    const match = matchUsageLimitBanner(text);
    if (!match) return;
    this.limitBannerReported = true;
    this.pushEvent({
      type: "rate_limit_event",
      rate_limit_info: {
        status: "rejected",
        rateLimitType: match.rateLimitType,
        ...(match.resetsAtEpochSeconds === undefined
          ? {}
          : { resetsAt: match.resetsAtEpochSeconds }),
      },
    });
  }

  private async finalizeOnExit(exitCode: number): Promise<void> {
    // Ship any buffered terminal tail (exit message, final repaint) before the
    // relay goes quiet — runs even on the teardown path, where dispose() below
    // may already have flushed (flush is a no-op when empty).
    this.coalescer?.flush();
    // The process is gone: block any reuse of this session regardless of which
    // path we take below.
    this.exited = true;
    // A teardown-driven kill (stop/softStop/abort) fires this exit handler, but
    // the turn is already discarded — pushing a synthetic error result here
    // would be spurious and would race teardown()'s queue close.
    if (this._toreDown) return;
    if (this.tailer) {
      this.tailer.close();
      await this.tailer.flush();
    }
    if (!this.adapter.capabilities.structuredEvents) {
      // Raw terminal mode: process exit IS the end-of-turn signal (there is no
      // transcript `result`). A clean exit is a successful turn; any other code
      // folds the terminal tail into an error result. endTurn closes the turn's
      // stream — the exit bookkeeping below still runs for both paths.
      if (exitCode === 0) {
        this.pushEvent({ type: "result", subtype: "success", result: "", total_cost_usd: 0 });
      } else {
        this.pushEvent({
          type: "result",
          subtype: "error",
          errors: this.adapter.buildExitErrors(exitCode, this.recentOutput),
        });
      }
      this.endTurn(exitCode === 0);
    } else if (!this.sawResult && this.activeQueue) {
      // A mid-turn crash with no result: fold the swallowed terminal tail into an
      // error result so a bare exit (missing `claude` binary, auth stop, unknown
      // flag) is diagnosable from the logs rather than only the live S5 terminal.
      // Only meaningful while a turn is draining — a parked-window death has no
      // consumer, and the harness's exit listener handles teardown + ended there.
      this.activeQueue.push({
        type: "result",
        subtype: "error",
        errors: this.adapter.buildExitErrors(exitCode, this.recentOutput),
      });
    }
    for (const listener of this.exitListeners) listener(exitCode);
    this.activeQueue?.close();
    this.activeQueue = null;
  }
}
